import { InputGroup } from '@chakra-ui/react'
import { Flex, Input } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { KEYPAD_INPUT_ATTRIBUTE } from '~/pages/WebPhone/utils/keyboardUtils'
import { getCaretPositionAfterFormatting } from '~/pages/WebPhone/utils/stringUtils'
import { Size } from '~/types/ThemeTypes'

import {
  NumberInputVariants,
  useNumberInputStyles,
} from './componentStyles/NumberInput.style'

interface NumberInputProps {
  phonenumber: string
  outboundCallerIdName?: string
  onPhonenumberChange: (newPhoneNumber: string) => void
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
    outboundCallerIdName?: string,
  ) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  disabled?: boolean
  callDisabled?: boolean
}
export const PhoneNumberInput = ({
  phonenumber,
  outboundCallerIdName,
  onPhonenumberChange,
  onStartCall,
  inputRef,
  disabled,
  callDisabled,
}: NumberInputProps) => {
  const { t } = useTranslation()
  const [cursorPosition, setCursorPostion] = React.useState(0)
  const [previousInputValue, setPreviousInputValue] = React.useState<string>('')

  // Set input box cursor position when phonenumber changes.
  // It must compensate for formatting updates to the phonenumber.
  // Depends on cursorPosition and previousInputValue
  useEffect(() => {
    if (inputRef.current) {
      const caret = getCaretPositionAfterFormatting(
        previousInputValue,
        cursorPosition,
        phonenumber,
      )
      inputRef.current.selectionStart = caret
      inputRef.current.selectionEnd = caret
    }
  }, [cursorPosition, previousInputValue])

  const updatePhonenumber = (newPhonenumber: string) => {
    setPreviousInputValue(newPhonenumber)
    onPhonenumberChange(newPhonenumber)
  }
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updatePhonenumber(e.target.value)
    setCursorPostion(e.target.selectionStart ?? 0)
  }

  const handleClear = () => {
    onPhonenumberChange('')
  }

  const variant = disabled
    ? NumberInputVariants.Disabled
    : phonenumber
      ? NumberInputVariants.Filled
      : NumberInputVariants.Ready
  const styles = useNumberInputStyles({
    variant,
  })
  return (
    <Flex css={styles.container}>
      <InputGroup
        endElement={
          <MangoButton
            onClick={handleClear}
            variant={MangoButtonVariant.Default_100}
            size={Size.sm}
            css={styles.inputRightElement}
          >
            {t('phoneNumberInput.clearButton')}
          </MangoButton>
        }
      >
        <Input
          css={styles.input}
          ref={inputRef}
          value={phonenumber}
          variant="flushed"
          aria-label={t('phoneNumberInput.inputLabel')}
          {...{ [KEYPAD_INPUT_ATTRIBUTE]: '' }}
          placeholder={t('phoneNumberInput.placeholder')}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !callDisabled) {
              onStartCall(phonenumber, outboundCallerIdName)
            }
          }}
        />
      </InputGroup>
    </Flex>
  )
}
